import {
  DateField,
  List,
  NumberField,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useSelect,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  FormProps,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
  useApiUrl,
  useCustom,
} from "@refinedev/core";

import dayjs from "dayjs";
import { IInvoiceReport, IShipco, IVessel } from "interfaces";
import uniqueId from "lodash/uniqueId";
import Papa from "papaparse";
import React from "react";

export const InvoiceReportList: React.FC<IResourceComponentsProps> = () => {
  const [dateSelected, setDateSelected] = React.useState<boolean>(false);
  const [orderDate, setOrderDate] = React.useState(null);
  const [exportFilter, setExportFilter] = React.useState<CrudFilters>([]);
  const [vesselNameFilter, setVesselNameFilter] = React.useState("");
  const [shipcoNameFilter, setShipcoNameFilter] = React.useState("");
  const [orderDateStartFilter, setOrderDateStartFilter] = React.useState("");
  const [orderDateEndFilter, setOrderDateEndFilter] = React.useState("");

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IInvoiceReport>({
      resource: "invoice",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { vesselName, shipcoName, orderDate } = params;

        filters.push({
          field: "vesselName",
          operator: "contains",
          value: vesselName ? vesselName : undefined,
        });
        setVesselNameFilter(vesselName ? vesselName : "");

        filters.push({
          field: "shipcoName",
          operator: "contains",
          value: shipcoName ? shipcoName : undefined,
        });
        setShipcoNameFilter(shipcoName ? shipcoName : "");

        if (orderDate) {
          filters.push({
            field: "orderDate",
            operator: "gte",
            value: orderDate
              ? orderDate[0].startOf("day").toISOString()
              : undefined,
          });
          setOrderDateStartFilter(
            orderDate ? orderDate[0].startOf("day").toISOString() : ""
          );

          filters.push({
            field: "orderDate",
            operator: "lte",
            value: orderDate
              ? orderDate[1].endOf("day").toISOString()
              : undefined,
          });
          setOrderDateEndFilter(
            orderDate ? orderDate[1].endOf("day").toISOString() : ""
          );
        }

        setExportFilter(filters);

        return filters;
      }
    });

  const apiUrl = useApiUrl();

  const {
    query: {
      isLoading: exportDataLoading
    },

    result: exportedData
  } = useCustom({
    url: `${apiUrl}/invoice/export`,
    method: "get",
    config: {
      query: {
        vesselName: vesselNameFilter,
        shipcoName: shipcoNameFilter,
        orderDateStart: orderDateStartFilter,
        orderDateEnd: orderDateEndFilter,
      },
    },
  });

  function handleDateRangeSelection(value: any) {
    setDateSelected(true);
    setOrderDate(value);
    searchFormProps?.form?.submit();
    searchFormProps?.form?.resetFields(["vesselName", "shipcoName"]);

    //special case as "orderDate is not reset with form.resetFields() above"
    // searchFormProps?.form?.setFieldsValue({ orderDate: undefined });
  }

  const columns: ColumnsType<IInvoiceReport> = [
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("vesselName", sorters),
    },
    {
      title: "Shipco",
      dataIndex: "shipcoName",
      key: "shipcoName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("shipcoName", sorters),
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 140,
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderDate", sorters),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 100,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("currency", sorters),
    },
    {
      title: "Guide Price",
      dataIndex: "guidePrice",
      key: "guidePrice",
      width: 130,
      render: (value) => (
        <NumberField
          value={value}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ),
      defaultSortOrder: getDefaultSortOrder("guidePrice", sorters),
    },
    {
      title: "UKHO Order ID",
      dataIndex: "ukhoOrderId",
      key: "ukhoOrderId",
      width: 150,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("ukhoOrderId", sorters),
    },
  ];

  function exportData(value: any) {
    //parse to csv
    var csv = Papa.unparse(exportedData?.data as any, {
      header: false,
      columns: [
        "orderDate",
        "orderStatus",
        "ukhoOrderId",
        "purchaseOrderNumber",
        "imoNumber",
        "vesselName",
        "shipcoName",
        "productId",
        "productName",
        "productGroup",
        "currency",
        "guidePrice",
        "subscriptionName",
        "holdStatus",
        "avcsSubscriptionType",
        "enpSubscriptionType",
        "adpSubscriptionType",
        "arcsSubscriptionType",
        "avcsDiscount",
        "enpDiscount",
        "adpDiscount",
        "arcsDiscount",
      ],
    });
    var headers = Papa.unparse({
      fields: [
        "Order Date",
        "Order Status",
        "UKHO Order ID",
        "Purchase Order Number",
        "IMO Number",
        "Vessel Name",
        "Shipco Name",
        "Product ID",
        "Product Name",
        "Product Group",
        "Currency",
        "Guide Price",
        "Subscription Name",
        "Hold Status",
        "AVCS Subscription Type",
        "ENP Subscription Type",
        "ADP Subscription Type",
        "ARCS Subscription Type",
        "AVCS Discount",
        "ENP Discount",
        "ADP Discount",
        "ARCS Discount",
      ],
      data: [],
    });
    var data = headers + csv;

    //download file
    if (typeof window === "undefined") {
      return;
    }
    const blob = new Blob([data], { type: `text/csv;charset=utf8;` });
    const link = document.createElement("a");
    link.setAttribute("visibility", "hidden");
    link.download = `invoice_report_${uniqueId()}`;
    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // As per documentation, call URL.revokeObjectURL to remove the blob from memory.
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    });
  }

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "Report",
        subTitle: "invoice",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <Button
                icon={<Icons.ExportOutlined />}
                onClick={exportData}
                disabled={!dateSelected}
                loading={exportDataLoading}
              >
                Export
              </Button>
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={6} lg={24} xs={24}>
          <Card type="inner" title={"Filter"}>
            <Filter
              formProps={searchFormProps}
              filters={filters || []}
              dateSelected={dateSelected}
              onDateRangeSelect={handleDateRangeSelection}
            />
          </Card>
        </Col>
        <Col xl={18} xs={24}>
          {dateSelected ? (
            <Table
              {...tableProps}
              columns={columns}
              rowKey={uniqueId()}
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          ) : (
            <Space
              direction="vertical"
              size="small"
              style={{
                display: "flex",
              }}
            >
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
              <Alert
                message="Select Date Range"
                description="Please select a date range to view the data"
                type="info"
                showIcon
                style={{
                  margin: "auto",
                  width: "40%",
                  padding: "15px",
                  textAlign: "center",
                  // alignItems: "center",
                }}
              />
            </Space>
          )}
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  dateSelected: boolean;
  onDateRangeSelect: any;
}> = (props) => {
  const { formProps, filters, dateSelected, onDateRangeSelect } = props;

  const { selectProps: vesselNameSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server",
      pageSize: 9
    }
  });

  const { selectProps: shipcoNameSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server",
      pageSize: 9
    }
  });

  const { RangePicker } = DatePicker;

  const orderDate = React.useMemo(() => {
    const start = getDefaultFilter("orderDate", filters, "gte");
    const end = getDefaultFilter("orderDate", filters, "lte");

    const startFrom = dayjs(start);
    const endAt = dayjs(end);

    if (start && end) {
      return [startFrom, endAt];
    }
    return undefined;
  }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        vesselName: getDefaultFilter("vesselName", filters),
        shipcoName: getDefaultFilter("shipcoName", filters),
        orderDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item
            label={"Order Date"}
            name="orderDate"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              onChange={onDateRangeSelect}
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item label={"Vessel Name"} name="vesselName">
            <Select
              {...vesselNameSelectProps}
              allowClear
              // mode="multiple"
              placeholder="filter by vessel"
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item label={"Shipco Name"} name="shipcoName">
            <Select
              {...shipcoNameSelectProps}
              allowClear
              // mode="multiple"
              placeholder="filter by shipco"
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
